"use client"

import type React from "react"
import { createContext, useContext, useState, useEffect } from "react"
import { listBillableActions } from "@/src/utils/billingApi"

/**
 * ⚠️  IMPORTANT: INFORMATIONAL USE ONLY ⚠️
 *
 * This context is designed EXCLUSIVELY for displaying billing information to users.
 * It does NOT handle actual billing, charging, or payment processing.
 *
 * Purpose:
 * - Show users the cost of actions BEFORE they perform them
 * - Display whether billing actions are currently active/available
 * - Provide UI feedback about credit costs across the application
 *
 * What this context DOES:
 * ✅ Fetches billing action metadata (costs, names, active status)
 * ✅ Caches this information for performance
 * ✅ Provides helper methods to look up costs by action name
 * ✅ Refreshes data periodically to stay current
 *
 * What this context DOES NOT DO:
 * ❌ Process actual payments or charges
 * ❌ Deduct credits from user accounts
 * ❌ Handle billing logic or transactions
 * ❌ Authorize or block user actions based on credits
 *
 * The actual billing/charging happens in the API routes when actions are performed.
 * This context only provides the UI with cost information for display purposes.
 */

// Type definitions for billable action information (INFORMATIONAL ONLY)
export type BillableActionInfo = {
  name: string // Action identifier (e.g., "GENERATE_CANVASES")
  cost: number // Credit cost to display to user (INFORMATIONAL)
  active: boolean // Whether this action is currently available (INFORMATIONAL)
  description?: string // Human-readable description (INFORMATIONAL)
}

// Context value type - all methods are for INFORMATIONAL DISPLAY only
type BillableActionsContextValue = {
  // Current billing action information (INFORMATIONAL ONLY)
  actions: BillableActionInfo[] | null

  // Loading state for UI feedback
  isLoading: boolean

  // Error state for graceful degradation
  error: string | null

  // When data was last refreshed (for cache management)
  lastUpdated: Date | null

  // Manual refresh function (does NOT affect actual billing)
  refreshActions: (silent?: boolean) => Promise<void>

  // Helper to get cost for display purposes (INFORMATIONAL ONLY)
  getActionCost: (actionName: string) => number

  // Helper to check if action is available for display (INFORMATIONAL ONLY)
  isActionActive: (actionName: string) => boolean

  // Helper to get action info for display (INFORMATIONAL ONLY)
  getActionInfo: (actionName: string) => BillableActionInfo | null
}

// Create the context with default values
const BillableActionsContext = createContext<BillableActionsContextValue>({
  actions: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
  refreshActions: async () => {},
  getActionCost: () => 0,
  isActionActive: () => true,
  getActionInfo: () => null,
})

/**
 * BillableActionsProvider - Provides billing information for UI display
 *
 * ⚠️  CRITICAL: This provider is for INFORMATIONAL PURPOSES ONLY
 *
 * It fetches and caches billing action metadata to help the UI show users
 * what actions cost before they perform them. It does NOT handle actual
 * billing, payments, or credit deductions.
 *
 * Cache Strategy:
 * - Data is cached in localStorage for 30 minutes (hard-coded for reliability)
 * - Background refresh every 30 minutes to keep costs current
 * - Immediate refresh when user authentication changes
 * - Graceful fallback if API calls fail
 *
 * Error Handling:
 * - Retries failed requests with exponential backoff
 * - Falls back to cached data if available
 * - Provides default values (0 cost, active) if no data available
 * - Never blocks UI functionality due to billing info failures
 */
export function BillableActionsProvider({ children }: { children: React.ReactNode }) {
  // State for billing action information (INFORMATIONAL ONLY)
  const [actions, setActions] = useState<BillableActionInfo[] | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  // Cache configuration (hard-coded to avoid env variable issues)
  const CACHE_KEY = "roomvia-billable-actions-cache"
  const CACHE_TIME_KEY = "roomvia-billable-actions-time"
  const CACHE_DURATION = 30 * 60 * 1000 // 30 minutes - hard-coded for reliability
  const MAX_RETRIES = 3
  const RETRY_DELAYS = [1000, 3000, 10000] // 1s, 3s, 10s exponential backoff

  /**
   * Load billing information from localStorage cache (INFORMATIONAL ONLY)
   *
   * This function checks if we have recent billing action data cached locally.
   * The cached data is ONLY used for displaying costs to users, never for
   * actual billing decisions or credit deductions.
   *
   * @returns true if valid cached data was loaded, false otherwise
   */
  const loadFromCache = (): boolean => {
    try {
      const cached = localStorage.getItem(CACHE_KEY)
      const cacheTime = localStorage.getItem(CACHE_TIME_KEY)

      if (cached && cacheTime) {
        const age = Date.now() - Number.parseInt(cacheTime)

        if (age < CACHE_DURATION) {
          const cachedActions = JSON.parse(cached) as BillableActionInfo[]
          setActions(cachedActions)
          setLastUpdated(new Date(Number.parseInt(cacheTime)))
          console.log("[BillableActions] ℹ️  Loaded cached billing info for display purposes")
          return true
        }
      }
    } catch (error) {
      console.warn("[BillableActions] ⚠️  Cache read failed, will fetch fresh data:", error)
    }
    return false
  }

  /**
   * Save billing information to localStorage cache (INFORMATIONAL ONLY)
   *
   * Persists the billing action data locally for faster UI display.
   * This cached data is ONLY used for showing costs to users.
   *
   * @param actionsData - The billing action information to cache
   */
  const saveToCache = (actionsData: BillableActionInfo[]): void => {
    try {
      const now = Date.now()
      localStorage.setItem(CACHE_KEY, JSON.stringify(actionsData))
      localStorage.setItem(CACHE_TIME_KEY, now.toString())
      setLastUpdated(new Date(now))
      console.log("[BillableActions] ℹ️  Cached billing info for display purposes")
    } catch (error) {
      console.warn("[BillableActions] ⚠️  Cache save failed:", error)
    }
  }

  /**
   * Fetch billing information with retry logic (INFORMATIONAL ONLY)
   *
   * This function fetches the latest billing action costs and availability
   * from the server. The data is ONLY used for UI display purposes.
   *
   * Includes exponential backoff retry logic for reliability.
   *
   * @param attempt - Current retry attempt number
   * @returns Promise resolving to billing action information
   */
  const fetchWithRetry = async (attempt = 0): Promise<BillableActionInfo[]> => {
    try {
      console.log(`[BillableActions] ℹ️  Fetching billing info for display (attempt ${attempt + 1})`)

      const response = await listBillableActions("")

      if (!response || !response.success || !Array.isArray(response.actions)) {
        throw new Error("Invalid API response structure")
      }

      const transformedActions: BillableActionInfo[] = response.actions.map((action) => {
        return {
          name: action.actionKey,
          cost: action.units,
          active: action.isActive,
          description: `${action.actionKey} action`, // Optional description
        }
      })

      setRetryCount(0)
      console.log("[BillableActions] ✅ Successfully fetched billing info for display")

      return transformedActions
    } catch (error) {
      console.error(`[BillableActions] ❌ Fetch attempt ${attempt + 1} failed:`)
      console.error("[v0] Error:", error instanceof Error ? error.message : String(error))

      if (attempt < MAX_RETRIES) {
        const delay = RETRY_DELAYS[attempt] || 10000
        console.log(`[BillableActions] ⏳ Retrying in ${delay}ms...`)

        await new Promise((resolve) => setTimeout(resolve, delay))
        return fetchWithRetry(attempt + 1)
      }

      setRetryCount(attempt + 1)
      throw error
    }
  }

  /**
   * Refresh billing action information (INFORMATIONAL ONLY)
   *
   * Fetches the latest billing costs and availability from the server.
   * This data is ONLY used to show users what actions cost - it does NOT
   * affect actual billing or credit deductions.
   *
   * @param silent - If true, doesn't show loading state (for background updates)
   */
  const refreshActions = async (silent = false): Promise<void> => {
    if (!silent) {
      setIsLoading(true)
    }

    setError(null)

    try {
      const actionsData = await fetchWithRetry()

      setActions(actionsData)
      saveToCache(actionsData)

      console.log("[BillableActions] ✅ Billing info refreshed for display purposes")
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to fetch billing information"
      setError(errorMessage)

      console.error("[BillableActions] ❌ Failed to refresh billing info:", error)

      if (actions && actions.length > 0) {
        console.log("[BillableActions] ℹ️  Using cached billing info for display")
      }
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Get the cost of a specific action for display purposes (INFORMATIONAL ONLY)
   *
   * This function returns the credit cost of an action for UI display.
   * It does NOT deduct credits or authorize actions - it's purely informational.
   *
   * @param actionName - The name of the billable action
   * @returns The credit cost to display to the user (0 if not found)
   */
  const getActionCost = (actionName: string): number => {
    if (!actions || !Array.isArray(actions)) {
      return 0
    }

    const action = actions.find((a) => a.name === actionName)
    const cost = action?.cost || 0

    return cost
  }

  /**
   * Check if a specific action is active for display purposes (INFORMATIONAL ONLY)
   *
   * This function returns whether an action is currently available for UI display.
   * It does NOT control actual access - it's purely informational.
   *
   * @param actionName - The name of the billable action
   * @returns Whether the action is active (true if not found - graceful degradation)
   */
  const isActionActive = (actionName: string): boolean => {
    if (!actions || !Array.isArray(actions)) {
      return true
    }

    const action = actions.find((a) => a.name === actionName)
    const active = action?.active ?? true

    return active
  }

  /**
   * Get complete action information for display purposes (INFORMATIONAL ONLY)
   *
   * Returns the full billing action information for UI display.
   * This data is ONLY for showing users action details.
   *
   * @param actionName - The name of the billable action
   * @returns The action information or null if not found
   */
  const getActionInfo = (actionName: string): BillableActionInfo | null => {
    if (!actions || !Array.isArray(actions)) {
      return null
    }

    const action = actions.find((a) => a.name === actionName) || null

    return action
  }

  useEffect(() => {
    console.log("[BillableActions] ℹ️  Initializing billing info context for display purposes")

    const hasValidCache = loadFromCache()

    if (!hasValidCache) {
      console.log("[BillableActions] ℹ️  No valid cache, fetching fresh billing info")
      refreshActions()
    }

    const refreshInterval = setInterval(() => {
      console.log("[BillableActions] ℹ️  Background refresh of billing info for display")
      refreshActions(true) // Silent refresh
    }, CACHE_DURATION)

    return () => {
      console.log("[BillableActions] ℹ️  Cleaning up billing info context")
      clearInterval(refreshInterval)
    }
  }, [])

  useEffect(() => {
    return () => {
      // Optional: Clear cache on unmount if needed
      // localStorage.removeItem(CACHE_KEY)
      // localStorage.removeItem(CACHE_TIME_KEY)
    }
  }, [])

  const contextValue: BillableActionsContextValue = {
    actions,
    isLoading,
    error,
    lastUpdated,
    refreshActions,
    getActionCost,
    isActionActive,
    getActionInfo,
  }

  return <BillableActionsContext.Provider value={contextValue}>{children}</BillableActionsContext.Provider>
}

/**
 * Hook to access billing action information for display purposes
 *
 * ⚠️  IMPORTANT: This hook provides data for INFORMATIONAL DISPLAY ONLY
 *
 * Use this hook in components that need to show users the cost of actions
 * before they perform them. The data returned is purely for UI display
 * and does NOT control actual billing or credit deductions.
 *
 * Example usage:
 * \`\`\`tsx
 * const { getActionCost, isActionActive } = useBillableActions()
 *
 * const cost = getActionCost('GENERATE_CANVASES') // For display only
 * const isActive = isActionActive('GENERATE_CANVASES') // For display only
 *
 * return (
 *   <Button disabled={!isActive}>
 *     Generate Canvas {cost > 0 && `(${cost} credits)`}
 *   </Button>
 * )
 * \`\`\`
 *
 * @returns BillableActionsContextValue - All methods are for display purposes only
 */
export function useBillableActions(): BillableActionsContextValue {
  const context = useContext(BillableActionsContext)

  if (!context) {
    throw new Error("useBillableActions must be used within a BillableActionsProvider")
  }

  return context
}

/**
 * ⚠️  FINAL REMINDER: INFORMATIONAL USE ONLY ⚠️
 *
 * This entire context and all its methods are designed exclusively for
 * displaying billing information to users. They do NOT:
 *
 * - Process payments or charges
 * - Deduct credits from accounts
 * - Control access to features
 * - Handle billing transactions
 * - Authorize or block user actions
 *
 * Actual billing happens in the API routes when actions are performed.
 * This context only provides cost information for UI display.
 */
